import React, { useEffect, useState, useCallback } from "react";
import { Button, Modal, Form, Input, Select, Table } from "antd";
import PageHeader from "../../components/PageHeader";
import Footer from "../../components/Footer";
import Breadcrumb from "../../components/Breadcrumb";
import {
  PageWrapper,
  DashboardMain,
  DashboardSection,
  ListHeader,
  ModuleCardTitle,
} from "./styles";
import { useContent, NotificationItem } from "../../hooks/content";
import useAdminGuard from "../../hooks/useAdminGuard";

const Notifications: React.FC = () => {
  useAdminGuard();
  const { listNotifications, saveNotification, removeNotification } =
    useContent();
  const [items, setItems] = useState<NotificationItem[]>([]);
  const [visible, setVisible] = useState(false);
  const [editing, setEditing] = useState<NotificationItem | null>(null);
  const [form] = Form.useForm();

  const load = useCallback(async () => {
    try {
      const data = await listNotifications();
      setItems(data);
    } catch (error) {
      console.error(error);
    }
  }, [listNotifications]);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ audience: "all" });
    setVisible(true);
  };

  const openEdit = (item: NotificationItem) => {
    setEditing(item);
    form.setFieldsValue({
      title: item.title,
      description: item.description,
      audience: item.audience,
    });
    setVisible(true);
  };

  const handleSubmit = async () => {
    const values = await form.validateFields();
    await saveNotification({
      uid: editing?.uid,
      title: values.title,
      description: values.description,
      audience: values.audience,
    });
    setVisible(false);
    await load();
  };

  const handleRemove = async (uid: string) => {
    await removeNotification(uid);
    await load();
  };

  const columns = [
    {
      title: "Título",
      dataIndex: "title",
      key: "title",
    },
    {
      title: "Mensagem",
      dataIndex: "description",
      key: "description",
      ellipsis: true,
    },
    {
      title: "Público",
      dataIndex: "audience",
      key: "audience",
    },
    {
      title: "Ações",
      key: "actions",
      render: (_: any, record: NotificationItem) => (
        <div style={{ display: "flex", gap: 8 }}>
          <Button size="small" type="primary" onClick={() => openEdit(record)}>
            Editar
          </Button>
          <Button size="small" danger onClick={() => handleRemove(record.uid)}>
            Excluir
          </Button>
        </div>
      ),
    },
  ];

  return (
    <PageWrapper id="page">
      <PageHeader
        title="Gerenciamento de Notificações"
        description="Envie e gerencie notificações para os usuários do aplicativo"
        type="private"
      />
      <DashboardMain className="dashboard-main">
        <Breadcrumb
          items={[
            { label: "Gerenciamento", link: "/manager" },
            { label: "Notificações", active: true },
          ]}
        />
        <DashboardSection className="dashboard-section">
          <ListHeader>
            <ModuleCardTitle className="module-card__title">
              Lista de Notificações
            </ModuleCardTitle>
            <Button type="primary" onClick={openCreate}>
              + Nova notificação
            </Button>
          </ListHeader>
          <Table
            rowKey="uid"
            columns={columns}
            dataSource={items}
            pagination={{ hideOnSinglePage: true, pageSize: 10 }}
          />
        </DashboardSection>
      </DashboardMain>
      <Footer />

      <Modal
        open={visible}
        title={editing ? "Editar notificação" : "Nova notificação"}
        onCancel={() => setVisible(false)}
        onOk={handleSubmit}
        okText="Salvar"
        cancelText="Cancelar"
      >
        <Form layout="vertical" form={form}>
          <Form.Item
            name="title"
            label="Título"
            rules={[{ required: true, message: "Informe o título" }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="description"
            label="Mensagem"
            rules={[{ required: true, message: "Informe a mensagem" }]}
          >
            <Input.TextArea rows={4} />
          </Form.Item>
          <Form.Item
            name="audience"
            label="Público"
            rules={[{ required: true, message: "Selecione o público" }]}
          >
            <Select
              options={[
                { value: "all", label: "Todos os usuários" },
                { value: "students", label: "Apenas alunos" },
                { value: "teachers", label: "Apenas professores" },
              ]}
            />
          </Form.Item>
        </Form>
      </Modal>
    </PageWrapper>
  );
};

export default Notifications;
